import React, { useEffect, useState } from 'react'
import { ActionIcon, Badge, Box, Button, Group, Paper, ScrollArea, Text, Transition } from '@mantine/core'
import { IconChevronRight, IconList, IconX } from '@tabler/icons-react'
import { getModuleInfo } from './ModuleUtil'
import { ThemeConfig } from '../../utils/themeConfig'

interface ModuleNavigationButtonProps {
  onClick: () => void
  themeConfig: ThemeConfig
  moduleCount: number
  className?: string
}

export const ModuleNavigationButton: React.FC<ModuleNavigationButtonProps> = ({
  onClick,
  themeConfig,
  moduleCount,
  className,
}) => {
  return (
    <div
      className={`flex flex-col items-center justify-center cursor-pointer p-3 transition-all duration-300 hover:scale-105 relative ${className || ''}`}
      onClick={onClick}
      style={{ backgroundColor: themeConfig.backgroundColor }}
    >
      <IconList size={26} color={themeConfig.primaryColor} />
      <span className="text-gray-500 text-xs font-medium mt-1">报告目录</span>
      {moduleCount > 0 && (
        <Badge
          size="xs"
          variant="filled"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: themeConfig.primaryColor,
          }}
        >
          {moduleCount}
        </Badge>
      )}
    </div>
  )
}

interface ModuleNavigationProps {
  data: any
  themeConfig: ThemeConfig
  opened: boolean
  setOpened: (opened: boolean) => void
}

export default function ModuleNavigation({ data, themeConfig, opened, setOpened }: ModuleNavigationProps) {
  const [activeModule, setActiveModule] = useState<string>('')
  const modules = getModuleInfo(data)

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100

      for (const module of modules) {
        const element = document.getElementById(`module-${module.key}`)
        if (element) {
          const { top, bottom } = element.getBoundingClientRect()
          const elementTop = top + window.scrollY
          const elementBottom = bottom + window.scrollY

          if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
            setActiveModule(module.key)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [modules])

  const scrollToModule = (moduleKey: string) => {
    const element = document.getElementById(`module-${moduleKey}`)
    if (element) {
      const offset = 80
      const elementPosition = element.getBoundingClientRect().top + window.scrollY
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })

      setTimeout(() => setOpened(false), 300)
    }
  }

  if (!modules.length) return null

  return (
    <>
      {/* Backdrop */}
      <Transition mounted={opened} transition="fade" duration={300}>
        {(styles) => (
          <div
            data-export-hidden
            style={{
              ...styles,
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
            onClick={() => setOpened(false)}
          />
        )}
      </Transition>

      {/* Slide-out Panel */}
      <Box
        data-export-hidden
        style={{
          position: 'fixed',
          top: 0,
          right: opened ? 0 : '-320px',
          bottom: 0,
          width: '300px',
          zIndex: 1000,
          transition: 'right 0.3s ease',
          backgroundColor: 'white',
          boxShadow: opened ? '-4px 0 20px rgba(0, 0, 0, 0.1)' : 'none',
        }}
      >
        <Paper
          p="md"
          radius={0}
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <Text size="lg" fw={600} style={{ color: themeConfig.primaryColor }}>
                导航菜单
              </Text>
              <Badge size="sm" variant="light" color={themeConfig.primaryColor}>
                {modules.length}
              </Badge>
            </Group>
            <ActionIcon onClick={() => setOpened(false)} size="sm" variant="subtle" color="gray" radius="xl">
              <IconX size={18} />
            </ActionIcon>
          </Group>

          {/* Module List */}
          <ScrollArea type="hover" scrollbarSize={6} style={{ flex: 1 }}>
            <div style={{ paddingRight: '12px' }}>
              {modules.map((module, index) => (
                <Button
                  key={module.key}
                  onClick={() => scrollToModule(module.key)}
                  fullWidth
                  justify="space-between"
                  variant={activeModule === module.key ? 'filled' : 'subtle'}
                  size="md"
                  mb="xs"
                  radius="md"
                  rightSection={
                    activeModule === module.key ? (
                      <IconChevronRight size={18} />
                    ) : (
                      <Text size="xs" c="dimmed">
                        {String(index + 1).padStart(2, '0')}
                      </Text>
                    )
                  }
                  styles={{
                    root: {
                      backgroundColor: activeModule === module.key ? themeConfig.primaryColor : 'transparent',
                      color: activeModule === module.key ? 'white' : 'inherit',
                      border: activeModule === module.key ? 'none' : `1px solid ${themeConfig.primaryColor}20`,
                      transition: 'all 0.2s ease',
                      textAlign: 'left',
                      '&:hover': {
                        backgroundColor:
                          activeModule === module.key ? themeConfig.primaryColor : `${themeConfig.primaryColor}10`,
                        transform: 'translateX(-4px)',
                      },
                    },
                  }}
                >
                  <Text size="sm" fw={activeModule === module.key ? 500 : 400}>
                    {module.name}
                  </Text>
                </Button>
              ))}
            </div>
          </ScrollArea>

          {/* Footer */}
          <Text size="xs" c="dimmed" mt="md" ta="center">
            点击模块快速跳转
          </Text>
        </Paper>
      </Box>
    </>
  )
}
